import React from 'react';
import { isEqual } from 'lodash';
import {
  CoachAvailabilityInput,
  CoachAvailabilityExceptionInput,
  CoachAvailabilityInterval,
  DayOfWeek,
} from 'globalTypes';
import {
  GetCoachAvailabilities_coaching_coachAvailability_availabilities,
  GetCoachAvailabilities_coaching_coachAvailability_availabilityExceptions,
} from 'coaching/queries/__generated__/GetCoachAvailabilities';
import CoachingAvailabilityAddTimeOffView from './CoachingAvailabilityAddTimeOffView';
import CoachingAvailabilityEditView from './CoachingAvailabilityEditView';
import CoachingAvailabilityReadView from './CoachingAvailabilityReadView';
import CoachingConstants from 'coaching/CoachingConstants';
import CoachingView from 'coaching/components/CoachingView';
import { constructAvailabilitiesByDay } from 'coaching/lib/coachAvailability';

export type Props = {
  existingAvailabilities?: (GetCoachAvailabilities_coaching_coachAvailability_availabilities | null)[];
  existingAvailabilityExceptions?: (GetCoachAvailabilities_coaching_coachAvailability_availabilityExceptions | null)[];
  setCoachingAvailability: (
    input: CoachAvailabilityInput[],
    timezone: string,
    onMutate?: () => void
  ) => void;
  setCoachingAvailabilityException: (
    input: CoachAvailabilityExceptionInput[],
    timezone: string,
    onMutate?: () => void
  ) => void;
  deleteCoachingAvailabilityException: (exceptionUuid: string) => void;
  weekOffset: number;
  onAdvanceWeekOffset: () => void;
  onRegressWeekOffset: () => void;
  timezone: string;
  loadingSchedule: boolean;
  errorMessage: string | undefined;
};

type State = {
  availabilitiesByDay: CoachAvailabilityInput[];
  editAvailabilities: boolean;
  showAddTimeOffView: boolean;
  timezone: string;
};

export default class CoachingAvailabilityView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      availabilitiesByDay: [],
      editAvailabilities: false,
      showAddTimeOffView: false,
      timezone: this.props.timezone,
    };
  }

  componentDidMount() {
    const { existingAvailabilities, loadingSchedule } = this.props;
    const availabilitiesByDay = constructAvailabilitiesByDay(existingAvailabilities);

    // default to the edit view if the coach has not set any availabilities yet
    const editAvailabilities =
      !loadingSchedule && (!existingAvailabilities || existingAvailabilities.length === 0);

    this.setState({
      editAvailabilities,
      availabilitiesByDay,
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { existingAvailabilities, timezone } = this.props;

    if (
      isEqual(prevProps.existingAvailabilities, existingAvailabilities) &&
      isEqual(prevProps.timezone, timezone)
    ) {
      return;
    }

    const availabilitiesByDay = constructAvailabilitiesByDay(existingAvailabilities);
    this.setState({
      availabilitiesByDay,
      timezone,
    });

    // if user is already looking at the edit availability view, don't recalculate
    // the edit availability state
    if (prevState.editAvailabilities !== true) {
      // default to the edit view if the coach has not set any availabilities yet
      const editAvailabilities = !existingAvailabilities || existingAvailabilities.length === 0;
      this.setState({ editAvailabilities });
    }
  }

  showEditView = () => {
    this.setState({ editAvailabilities: true });
  };

  showReadView = () => {
    this.setState({ editAvailabilities: false });
  };

  showAddTimeOffView = () => {
    this.setState({ showAddTimeOffView: true });
  };

  hideAddTimeOffView = () => {
    this.setState({ showAddTimeOffView: false });
  };

  onTimezoneChange = (newTimezone: string) => {
    this.setState({ timezone: newTimezone });
  };

  updateAvailabilitiesByDay = (dayOfWeek: DayOfWeek, intervals: CoachAvailabilityInterval[]) => {
    const dayOfWeekIndex = CoachingConstants.SORTED_DAYS_OF_WEEK.indexOf(dayOfWeek);
    const { availabilitiesByDay } = this.state;
    const availabilityUuid = availabilitiesByDay[dayOfWeekIndex]?.uuid ?? '';

    // construct the updated availability
    const updatedAvailability = {
      uuid: availabilityUuid,
      timeSlot: {
        dayOfWeek,
        intervals,
      },
    };

    // replace the availability
    availabilitiesByDay.splice(dayOfWeekIndex, 1, updatedAvailability);
    this.setState({
      availabilitiesByDay,
    });
  };

  renderEditView = () => {
    const { timezone } = this.state;
    const { availabilitiesByDay } = this.state;
    return (
      <CoachingAvailabilityEditView
        availabilitiesByDay={availabilitiesByDay}
        timezone={timezone}
        onTimezoneChange={this.onTimezoneChange}
        onAvailabilityChange={(intervals: CoachAvailabilityInterval[], day: DayOfWeek) =>
          this.updateAvailabilitiesByDay(day, intervals)
        }
        onClickSetAvailability={() =>
          this.props.setCoachingAvailability(availabilitiesByDay, timezone, this.showReadView)
        }
      />
    );
  };

  renderReadView = () => {
    const {
      loadingSchedule,
      errorMessage,
      weekOffset,
      existingAvailabilityExceptions,
    } = this.props;
    const { availabilitiesByDay } = this.state;

    return (
      <CoachingAvailabilityReadView
        errorMessage={errorMessage}
        loadingSchedule={loadingSchedule}
        existingAvailabilities={availabilitiesByDay}
        existingAvailabilityExceptions={existingAvailabilityExceptions}
        weekOffset={weekOffset}
        onClickLeftChevron={this.props.onRegressWeekOffset}
        onClickRightChevron={this.props.onAdvanceWeekOffset}
        onClickEditLink={this.showEditView}
        onClickAddTimeOff={this.showAddTimeOffView}
        deleteCoachingAvailabilityException={this.props.deleteCoachingAvailabilityException}
      />
    );
  };

  renderAddTimeOffView = () => {
    return (
      <CoachingAvailabilityAddTimeOffView
        weekOffset={this.props.weekOffset}
        onHide={this.hideAddTimeOffView}
        onAddTimeOff={this.props.setCoachingAvailabilityException}
      />
    );
  };

  render() {
    const availabilityView = this.state.editAvailabilities
      ? this.renderEditView()
      : this.renderReadView();
    return (
      <CoachingView>
        {this.state.showAddTimeOffView ? this.renderAddTimeOffView() : availabilityView}
      </CoachingView>
    );
  }
}
